import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, Check, CheckCheck, Trash2, Settings, Package, Truck, 
  CreditCard, AlertCircle, UserPlus, MessageSquare, Clock,
  Loader2, Wifi, WifiOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { formatDistanceToNow } from 'date-fns';

// Notification type icons and colors
const NOTIFICATION_CONFIG = {
  order_confirmed: { icon: Package, color: 'text-green-500', bg: 'bg-green-100' },
  order_preparing: { icon: Clock, color: 'text-orange-500', bg: 'bg-orange-100' },
  order_ready: { icon: Check, color: 'text-green-500', bg: 'bg-green-100' },
  order_out_for_delivery: { icon: Truck, color: 'text-blue-500', bg: 'bg-blue-100' },
  order_delivered: { icon: CheckCheck, color: 'text-green-500', bg: 'bg-green-100' },
  driver_arriving: { icon: Truck, color: 'text-blue-500', bg: 'bg-blue-100' },
  new_order: { icon: Package, color: 'text-orange-500', bg: 'bg-orange-100' },
  new_delivery: { icon: Truck, color: 'text-green-500', bg: 'bg-green-100' },
  payout_processed: { icon: CreditCard, color: 'text-green-500', bg: 'bg-green-100' },
  earnings_payout: { icon: CreditCard, color: 'text-green-500', bg: 'bg-green-100' },
  account_approved: { icon: UserPlus, color: 'text-green-500', bg: 'bg-green-100' },
  new_signup: { icon: UserPlus, color: 'text-blue-500', bg: 'bg-blue-100' },
  support_ticket: { icon: MessageSquare, color: 'text-purple-500', bg: 'bg-purple-100' },
  system_alert: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-100' },
  admin_broadcast: { icon: Bell, color: 'text-orange-500', bg: 'bg-orange-100' },
  table_order: { icon: Package, color: 'text-orange-500', bg: 'bg-orange-100', action: '/restaurant/live-tables' },
  waiter_request: { icon: Bell, color: 'text-red-500', bg: 'bg-red-100', action: '/restaurant/live-tables' },
  booking: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-100', action: '/restaurant/tables' },
  bill: { icon: CreditCard, color: 'text-green-500', bg: 'bg-green-100' },
  default: { icon: Bell, color: 'text-slate-500', bg: 'bg-slate-100' },
};

export default function NotificationsPage() {
  const { api, token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // WebSocket connection for real-time updates
  const connectWebSocket = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    if (!wsUrl) return;

    try {
      wsRef.current = new WebSocket(`${wsUrl}/ws/notifications?token=${token}`);

      wsRef.current.onopen = () => {
        setIsConnected(true);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'notification') {
            setNotifications(prev => [data.data, ...prev]);
          } else if (data.type === 'notification_read') {
            setNotifications(prev => 
              prev.map(n => n.id === data.notification_id ? { ...n, read: true } : n)
            );
          } else if (data.type === 'all_read') {
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      wsRef.current.onclose = () => {
        setIsConnected(false);
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [token]);

  useEffect(() => {
    connectWebSocket();
    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connectWebSocket]);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications?limit=50');
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'mark_read', notification_id: id }));
      } else {
        await api.post(`/notifications/${id}/read`);
      }
      setNotifications(notifications.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'mark_all_read' }));
      } else {
        await api.post('/notifications/read-all');
      }
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(notifications.filter(n => n.id !== id));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const clearAll = async () => {
    try {
      await api.delete('/notifications');
      setNotifications([]);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const getNotificationConfig = (type) => {
    return NOTIFICATION_CONFIG[type] || NOTIFICATION_CONFIG.default;
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return '';
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Use action_url from notification, or fallback to config action
    const config = getNotificationConfig(notification.type);
    const actionUrl = notification.action_url || config.action;
    
    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">Notifications</h1>
            {isConnected ? (
              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                <Wifi className="w-3 h-3" />
                Live
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                <WifiOff className="w-3 h-3" />
                Connecting...
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/notification-settings')}
              className="text-slate-600"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={markAllAsRead} 
                className="text-orange-500"
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                Mark all read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearAll} 
                className="text-red-500"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear all
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        {notifications.length > 0 && (
          <div className="bg-white rounded-xl p-4 mb-4 flex items-center justify-between">
            <span className="text-sm text-slate-500">
              {notifications.length} notifications • {unreadCount} unread
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : notifications.length > 0 ? (
          <div className="space-y-3">
            {notifications.map((notification) => {
              const config = getNotificationConfig(notification.type);
              const Icon = config.icon;
              
              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${
                    !notification.read ? 'border-l-4 border-orange-500 bg-orange-50/30' : ''
                  }`}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${config.bg}`}>
                      <Icon className={`w-6 h-6 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className={`font-semibold text-slate-900 ${!notification.read ? 'font-bold' : ''}`}>
                            {notification.title}
                          </h3>
                          <p className="text-sm text-slate-500 mt-1">{notification.message}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {!notification.read && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              className="text-slate-400 hover:text-orange-500"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            className="text-slate-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 mt-2">{formatTime(notification.created_at)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-xl">
            <Bell className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No notifications</h2>
            <p className="text-slate-500 mb-4">You're all caught up!</p>
            <Button variant="outline" onClick={() => navigate('/notification-settings')}>
              <Settings className="w-4 h-4 mr-2" />
              Notification Settings
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
